import { Trans } from '@lingui/react'
import classNames from 'classnames'
import React, { Fragment } from 'react'

import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import { getMetadataContentFromPlacement } from 'shared/utils/adServerUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import type {
  AdServerDecisionResponseEvent,
  AdSlotDimensions,
  Placement,
} from 'types/adServerTypes'
import {
  AdServerDecisionResponseContentType,
  AdServerPlacementTemplate,
} from 'types/adServerTypes'
import { CLIENTSIDE_HOOK_DATA_ATTR } from 'views/assets/scripts/consts'
import IconCross from 'views/components/atoms/Icon/IconCross'
import Image from 'views/components/atoms/Image'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import ScriptHandleBannerVisibility from 'views/components/utils/Script/ScriptHandleBannerVisibility'
import {
  AD_BANNER_AUTO_LOAD_DATA_ATTR,
  AD_BANNER_CAMPAIGN_NAME_DATA_ATTR,
  AD_BANNER_CLIENTSIDE_HOOK,
  AD_BANNER_CLOSE_CLIENTSIDE_HOOK,
  AD_BANNER_CONTENT_DATA_ATTR,
  AD_BANNER_HEIGHT_DATA_ATTR,
  AD_BANNER_STATUS_DATA_ATTR,
  AD_BANNER_TAGS_DATA_ATTR,
  AD_BANNER_WIDTH_DATA_ATTR,
} from 'views/consts/adBanner'
import setInnerHTML from 'views/utils/setInnerHTML'
import './AdBanner.scss'

interface AdBannerProps {
  placement?: Placement
  events?: Array<AdServerDecisionResponseEvent>
  dimensions: AdSlotDimensions
  className?: string
  optional?: boolean
  isAutoLoaded?: boolean
  renderBannerOnServerSide?: boolean
  bannerTags?: Array<string>
  matchDimensionsAttrs?: boolean
  closeId?: string
  campaignName?: string
}

const AdBanner = React.forwardRef<HTMLDivElement, AdBannerProps>(
  (props, ref) => {
    const {
      placement,
      placement: {
        body,
        width,
        height,
        template,
        imageUrl,
        clickUrl,
        title,
        type,
      } = {},
      dimensions,
      className,
      optional,
      isAutoLoaded,
      renderBannerOnServerSide,
      bannerTags,
      matchDimensionsAttrs,
      closeId,
      campaignName,
    } = props

    const classes = classNames(
      'as-placeholder',
      {
        'as-placeholder--optional': optional,
      },
      className
    )
    const Wrapper = closeId ? 'div' : Fragment
    const wrapperProps = closeId
      ? {
          className: 'u-position--relative u-no-overflow',
          id: closeId,
        }
      : {}

    const { width: slotWidth, height: slotHeight } = dimensions

    // using padding-top to set banner aspect ratio
    const paddingTop = `${100 / (slotWidth / slotHeight)}%`

    const renderRawTypeBannerOnServerSide =
      renderBannerOnServerSide &&
      type === AdServerDecisionResponseContentType.raw

    const renderImageTypeBannerOnServerSide =
      renderBannerOnServerSide &&
      template === AdServerPlacementTemplate.image &&
      type === AdServerDecisionResponseContentType.html &&
      imageUrl &&
      clickUrl

    const renderBannerOnClientSide = !renderBannerOnServerSide
    const includeAdServerMetadataAttr =
      !isEmpty(placement) &&
      (renderImageTypeBannerOnServerSide || renderRawTypeBannerOnServerSide)

    return (
      <Wrapper {...wrapperProps}>
        {closeId && (
          <>
            <script
              {...setInnerHTML(
                `(${ScriptHandleBannerVisibility.toString()}(${JSON.stringify(
                  closeId
                )}))`
              )}
            />
            <button
              {...{
                className: 'm-AdBanner__close',
                [CLIENTSIDE_HOOK_DATA_ATTR]: AD_BANNER_CLOSE_CLIENTSIDE_HOOK,
                type: 'button',
              }}
            >
              <TextWithIcon
                icon={<IconCross className="m-AdBanner__close-icon" />}
              >
                <span className="u-visuallyhidden">
                  <Trans id="general.link.label.close" />
                </span>
              </TextWithIcon>
            </button>
          </>
        )}
        <div
          {...{
            ref,
            className: classes,
            style: {
              paddingTop,
            },
            [AD_BANNER_AUTO_LOAD_DATA_ATTR]: isAutoLoaded ? 'true' : undefined,
            [AD_BANNER_STATUS_DATA_ATTR]: renderBannerOnClientSide
              ? ''
              : 'ready',
            [AD_BANNER_TAGS_DATA_ATTR]: bannerTags?.toString(),
            [AD_BANNER_WIDTH_DATA_ATTR]:
              optional || matchDimensionsAttrs ? slotWidth : undefined,
            [AD_BANNER_HEIGHT_DATA_ATTR]:
              optional || matchDimensionsAttrs ? slotHeight : undefined,
            [CLIENTSIDE_HOOK_DATA_ATTR]: renderBannerOnClientSide
              ? AD_BANNER_CLIENTSIDE_HOOK
              : undefined,
            [AD_SERVER_METADATA_DATA_ATTR]: includeAdServerMetadataAttr
              ? JSON.stringify(getMetadataContentFromPlacement(placement))
              : undefined,
            [AD_BANNER_CAMPAIGN_NAME_DATA_ATTR]: campaignName,
          }}
        >
          {/* Render Image|HTML Type AdBanner on Server Side*/}
          {renderImageTypeBannerOnServerSide && (
            <div
              {...{
                [AD_BANNER_CONTENT_DATA_ATTR]: '',
              }}
            >
              <a href={clickUrl}>
                <Image src={imageUrl} alt={title} />
              </a>
            </div>
          )}

          {/* Render Raw Type AdBanner on Server Side*/}
          {renderRawTypeBannerOnServerSide && (
            <div
              {...{
                [AD_BANNER_CONTENT_DATA_ATTR]: '',
              }}
            >
              <iframe
                title={title}
                sandbox="allow-top-navigation-by-user-activation"
                width={width}
                height={height}
                frameBorder={0}
                scrolling="no"
                src={`data:text/html,<body style="overflow:hidden;margin:0">${body}</body>`}
              />
            </div>
          )}
        </div>
      </Wrapper>
    )
  }
)

AdBanner.displayName = 'AdBanner'

AdBanner.defaultProps = {
  events: [],
  optional: false,
  isAutoLoaded: false,
  renderBannerOnServerSide: false,
  matchDimensionsAttrs: false,
  closeId: undefined,
  campaignName: undefined,
}
export default AdBanner
